"use client";

import CartItemModal from "@/components/ui/ecommerce/cart/CartItemModal";
import RecommendedProduct from "@/components/ui/ecommerce/cart/RecommendedProduct";
import { CloseIcon } from "@/components/ui/Icons";
import { getIp } from "@/utils/getCountryIp";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { fetchProductsByParentCategoryId } from "@/libs/data";
import { useCartStore2 } from "@/stores/cartStore";

interface CartModalProps {
  open: boolean;
  onClose: () => void;
}

// Componente Skeleton para los items del carrito
const CartItemSkeleton = () => (
  <div className="flex justify-between py-4 border-b animate-pulse">
    <div className="flex gap-5 items-center">
      <div className="w-20 h-20 bg-gray-200 rounded-md"></div>
      <div className="flex flex-col items-start justify-between gap-2">
        <div>
          <div className="h-6 bg-gray-200 rounded-sm w-40 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded-sm w-32"></div>
        </div>
        <div className="h-8 bg-gray-200 rounded-sm w-24 mt-2"></div>
      </div>
    </div>
    <div className="flex flex-col justify-between items-end">
      <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
      <div className="text-right">
        <div className="h-5 bg-gray-200 rounded-sm w-20"></div>
      </div>
    </div>
  </div>
);

export default function CartModal({ open, onClose }: CartModalProps) {
  const router = useRouter();
  const { fetchCart, cart2, loading, emptyCart } = useCartStore2();
  const [isEmptying, setIsEmptying] = useState(false);
  
  useEffect(() => {
    if (!cart2) {
      fetchCart();
    }
  }, [fetchCart, cart2]);
  
  const [mounted, setMounted] = useState(false);
  const [country, setCountry] = useState<string | null>(null);
  const [recommendedProducts, setRecommendedProducts] = useState<any[]>([]);

  useEffect(() => {
    async function fetchCountry() {
      const countryCode = await getIp();
      setCountry(countryCode);
    }
    fetchCountry();
  }, []);

  useEffect(() => {
    async function fetchRecommendedProducts() {
      try {
        const allProducts = await fetchProductsByParentCategoryId("8");
        // Usar cart2 en lugar de cart
        const cartItemIds = new Set(cart2?.items?.map((item) => item.product_id) || []);
        const filtered = allProducts.filter(
          (product: any) => !cartItemIds.has(product.id)
        );
        setRecommendedProducts(filtered.slice(0, 4));
      } catch (error) {
        setRecommendedProducts([]);
      }
    }
    if (cart2) {
      fetchRecommendedProducts();
    }
  }, [cart2]);

  const isUSD = country !== "CL" && country !== null;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Calcular el total usando cart2
  const cartTotal = cart2?.subtotal_amount || 0;

  // Calcular el número de items usando cart2
  const itemCount = cart2?.items?.reduce((acc, item) => acc + item.quantity, 0) || 0;

  const formatPrice = (price: number) => {
    return price.toLocaleString(isUSD ? "en-US" : "es-CL", {
      style: "currency",
      currency: isUSD ? "USD" : "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleEmptyCart = async () => {
    if (window.confirm("¿Estás seguro de que deseas vaciar el carrito?")) {
      setIsEmptying(true);
      try {
        await emptyCart();
      } catch (error) {
        //console.error("Error al vaciar el carrito:", error);
      } finally {
        setIsEmptying(false);
      }
    }
  };

  if (!mounted) return null;

  return (
    <div
      className={`fixed right-0 sm:right-4 z-50 w-full sm:w-96 md:w-[420px] bg-gray-100 rounded-t-md shadow-lg transition-all duration-500 ease-in-out ${
        open ? "animate-slideIn bottom-0" : "animate-slideOut -bottom-full"
      }`}
    >
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="text-lg font-semibold text-black">
          CARRITO <span className="text-gray-400">({loading ? "..." : itemCount})</span>
        </h3>
        <button
          className="bg-gray-200 p-2 rounded-full"
          onClick={onClose}
          aria-label="Cerrar carrito"
        >
          <CloseIcon className="fill-gray-400 w-4 h-4" />
        </button>
      </div>
      <div className="overflow-y-auto max-h-[500px] p-2 sm:p-3 md:p-4 custom-scrollbar">
        {loading ? (
          // Mostrar skeletons mientras carga
          <>
            <CartItemSkeleton />
            <CartItemSkeleton />
            <CartItemSkeleton />
          </>
        ) : cart2?.items && cart2.items.length > 0 ? (
          cart2.items.map((item: any) => (
            <CartItemModal
              key={`${item.product_id}-${item.variant_id || ""}`}
              product={{
                id: item.product_id,
                name: item.product.name,
                price: item.unit_amount,
                discounted_price: item.discount_amount,
                qty: item.quantity,
                thumbnail_url: item.variant?.thumbnail_url || item.product?.thumbnail_url,
                slug: item.product.slug,
                variant: item.variant?.selectedOptions?.map((opt: any) => ({
                  id: opt.option_id,
                  name: opt.variation.name,
                  value: opt.option.value,
                  hex_code: opt.option.hex_code
                })) || [],
                type: item.line_type,
                bundleProducts: item.components || [],
                cart_item_id: item.id,
              }}
            />
          ))
        ) : (
          <p className="text-center text-gray-500 py-4">
            Tu carrito está vacío
          </p>
        )}

        {cart2?.items && cart2.items.length > 0 && recommendedProducts.length > 0 && (
          <div className="mt-6">
            <h4 className="text-md font-semibold mb-2">
              Complementa tu compra
            </h4>
            <div className="space-y-2">
              {recommendedProducts.map((product) => (
                <RecommendedProduct key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col justify-between items-center p-4 border-t text-center gap-3">
        <small className="text-gray-500">
          Los gastos de envío se calculan en la página de pago.
        </small>
        <button
          className="w-full bg-bright-pink text-white p-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => {
            router.push("/payment");
            onClose();
          }}
          disabled={loading || !cart2?.items || cart2.items.length === 0 || isEmptying}
        >
          IR A PAGAR ▪ {loading || isEmptying ? "..." : formatPrice(cartTotal)}
        </button>
        
        {/* Botón para vaciar el carrito */}
        {cart2?.items && cart2.items.length > 0 && (
          <button
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors mt-1"
            onClick={handleEmptyCart}
            disabled={loading || isEmptying}
          >
            {isEmptying ? "Vaciando..." : "Vaciar carrito"}
          </button>
        )}
      </div>
      <Toaster />
    </div>
  );
}

